import { HttpErrorResponse } from '@angular/common/http';
import { Event, EventHint } from '@sentry/angular';
import { HttpStatus } from '../constants/const';

const connectionFailureMessage = /^Http failure response for (.+): 0 /;
const connectionRefusedTag = String(HttpStatus.connectionRefused);
const unknownEndpoint = 'unknown';
const maxEndpointSegments = 4;
const variableSegment = /[\d@%]/;

export const isConnectionError = (error: unknown): boolean =>
  error instanceof HttpErrorResponse && error.status === HttpStatus.connectionRefused;

const isConnectionFailure = (event: Event, originalException: unknown): boolean =>
  isConnectionError(originalException) ||
  (typeof originalException === 'string' && connectionFailureMessage.test(originalException)) ||
  event.tags?.status === connectionRefusedTag;

const failedUrlOf = (originalException: unknown): string | undefined => {
  if (originalException instanceof HttpErrorResponse) {
    return originalException.url ?? undefined;
  }
  if (typeof originalException === 'string') {
    return originalException.match(connectionFailureMessage)?.[1];
  }
  return undefined;
};

const normalizedPathOf = (url: string): string =>
  `/${new URL(url, location.origin).pathname
    .split('/')
    .filter((segment) => segment)
    .slice(0, maxEndpointSegments)
    .map((segment) => (variableSegment.test(segment) ? ':id' : segment))
    .join('/')}`;

export const endpointOf = (originalException: unknown): string => {
  const url = failedUrlOf(originalException);
  if (!url) {
    return unknownEndpoint;
  }
  try {
    return normalizedPathOf(url);
  } catch {
    return unknownEndpoint;
  }
};

export const tagConnectionErrors = <T extends Event>(event: T, hint?: EventHint): T => {
  if (!isConnectionFailure(event, hint?.originalException)) {
    return event;
  }
  const feature = event.tags?.feature;
  event.tags = { ...event.tags, connection: 'api-unreachable' };
  event.fingerprint = [
    'api-unreachable',
    feature ? String(feature) : endpointOf(hint?.originalException),
  ];
  return event;
};
